import { useState, useRef, useEffect } from 'react';

const DEFAULT_HEIGHTS = {
  'clock': 1,
  'analog-clock': 2,
  'tickers': 3,
  'upcoming-events': 4,
  'weather': 2,
  'hourly-weather': 2,
  'traffic': 1,
  'news': 2,
  'shopping': 2,
  'countdowns': 2,
  'goals': 4,
  'goals-chart': 2,
  'goals-history': 3,
  'calendar-month': 4,
  'airbnb-alerts': 1,
  'airbnb': 4,
  'airbnb-calendar': 4,
  'airbnb-earnings': 2,
  'airbnb-ytd': 2,
  'quote': 1,
  'receipts': 3,
  'receipt-records': 3,
  'receipt-exporter': 3,
  'packages': 3,
  'wedding-guests': 4,
  'wedding-dashboard': 4,
  'web-embed': 3,
};

export default function DashboardPanels({ panelMap, layout, layouts, onLayoutChange, editMode = false, onNavigate }) {
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [sizeMenuId, setSizeMenuId] = useState(null);
  const [moreMenuId, setMoreMenuId] = useState(null);
  const touchState = useRef(null);
  const panelRefs = useRef({});

  const [showMinimized, setShowMinimized] = useState(false);

  const order = layout.order;
  const hidden = layout.hidden;
  const minimized = layout.minimized || [];
  const columns = layout.columns;
  const heights = layout.heights || {};
  const widths = layout.widths || {};
  const moreLinks = layout.moreLinks || {};

  function getHeight(panelId) {
    if (panelId in heights) return heights[panelId];
    return DEFAULT_HEIGHTS[panelId] || 1;
  }

  function getWidth(panelId) {
    return Math.min(widths[panelId] || 1, columns);
  }

  function setHeight(panelId, h) {
    onLayoutChange({ heights: { ...heights, [panelId]: h } });
    setSizeMenuId(null);
  }

  function setWidth(panelId, w) {
    onLayoutChange({ widths: { ...widths, [panelId]: w } });
    setSizeMenuId(null);
  }

  function setMoreLink(panelId, layoutIdx) {
    const newLinks = { ...moreLinks };
    if (layoutIdx === null) {
      delete newLinks[panelId];
    } else {
      newLinks[panelId] = layoutIdx;
    }
    onLayoutChange({ moreLinks: newLinks });
    setMoreMenuId(null);
  }

  function saveOrder(newOrder) {
    onLayoutChange({ order: newOrder });
  }

  function hidePanel(panelId) {
    onLayoutChange({ hidden: [...hidden, panelId] });
  }

  function minimizePanel(panelId) {
    onLayoutChange({ minimized: [...minimized, panelId] });
  }

  function unminimizePanel(panelId) {
    onLayoutChange({ minimized: minimized.filter((id) => id !== panelId) });
  }

  const visibleOrder = order.filter((id) => !hidden.includes(id) && panelMap[id]);

  // Mouse drag and drop
  function handleDragStart(e, panelId) {
    setDragging(panelId);
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragOver(e, panelId) {
    e.preventDefault();
    if (dragging && panelId !== dragging) {
      setDragOver(panelId);
    }
  }

  function handleDrop(e, panelId) {
    e.preventDefault();
    if (dragging && panelId !== dragging) {
      const newOrder = [...order];
      const fromIdx = newOrder.indexOf(dragging);
      const toIdx = newOrder.indexOf(panelId);
      newOrder.splice(fromIdx, 1);
      newOrder.splice(toIdx, 0, dragging);
      saveOrder(newOrder);
    }
    setDragging(null);
    setDragOver(null);
  }

  function handleDragEnd() {
    setDragging(null);
    setDragOver(null);
  }

  // Touch drag and drop
  function handleTouchStart(e, panelId) {
    if (!editMode) return;
    const touch = e.touches[0];
    touchState.current = {
      panelId,
      startY: touch.clientY,
      startX: touch.clientX,
      moved: false,
    };
  }

  useEffect(() => {
    function handleTouchMove(e) {
      if (!touchState.current) return;
      const touch = e.touches[0];
      const dx = Math.abs(touch.clientX - touchState.current.startX);
      const dy = Math.abs(touch.clientY - touchState.current.startY);

      if (!touchState.current.moved && (dx > 10 || dy > 10)) {
        touchState.current.moved = true;
        setDragging(touchState.current.panelId);
      }

      if (touchState.current.moved) {
        e.preventDefault();
        const el = document.elementFromPoint(touch.clientX, touch.clientY);
        if (el) {
          const panelEl = el.closest('[data-panel-id]');
          if (panelEl) {
            const overId = panelEl.getAttribute('data-panel-id');
            if (overId !== touchState.current.panelId) {
              setDragOver(overId);
            }
          }
        }
      }
    }

    function handleTouchEnd() {
      if (touchState.current && touchState.current.moved && dragging && dragOver) {
        const newOrder = [...order];
        const fromIdx = newOrder.indexOf(dragging);
        const toIdx = newOrder.indexOf(dragOver);
        newOrder.splice(fromIdx, 1);
        newOrder.splice(toIdx, 0, dragging);
        saveOrder(newOrder);
      }
      touchState.current = null;
      setDragging(null);
      setDragOver(null);
    }

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
    return () => {
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  });

  const displayOrder = showMinimized
    ? visibleOrder
    : visibleOrder.filter((id) => !minimized.includes(id));

  const minimizedPanels = visibleOrder.filter((id) => minimized.includes(id));

  return (
    <div>
    <div
      className="panel-grid"
      style={{
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridAutoRows: '150px',
      }}
    >
      {displayOrder.map((panelId) => {
        const content = panelMap[panelId];
        if (!content) return null;
        const h = getHeight(panelId);
        const w = getWidth(panelId);
        const moreTarget = moreLinks[panelId];
        const hasMore = moreTarget !== undefined && moreTarget !== null && layouts && layouts[moreTarget];

        return (
          <div
            key={panelId}
            data-panel-id={panelId}
            ref={(el) => (panelRefs.current[panelId] = el)}
            className={`panel-wrapper${dragging === panelId ? ' panel-dragging' : ''}${dragOver === panelId ? ' panel-drag-over' : ''}${editMode ? ' edit-mode' : ''}${h === 0 ? ' panel-full-height' : ''}${minimized.includes(panelId) && showMinimized ? ' panel-minimized' : ''}`}
            style={h === 0
              ? { gridColumn: `span ${w}` }
              : { gridRow: `span ${h}`, gridColumn: `span ${w}` }
            }
            draggable={editMode}
            onDragStart={(e) => handleDragStart(e, panelId)}
            onDragOver={(e) => handleDragOver(e, panelId)}
            onDrop={(e) => handleDrop(e, panelId)}
            onDragEnd={handleDragEnd}
            onTouchStart={(e) => handleTouchStart(e, panelId)}
          >
            {editMode && (
              <div className="panel-controls">
                <span
                  className="panel-size-toggle"
                  onClick={(e) => { e.stopPropagation(); setSizeMenuId(sizeMenuId === panelId ? null : panelId); setMoreMenuId(null); }}
                  title="Resize"
                >
                  {h === 0 ? '100%' : h}x{w}
                </span>
                {sizeMenuId === panelId && (
                  <div className="panel-size-menu" onClick={(e) => e.stopPropagation()}>
                    <div className="size-menu-row">
                      <span className="size-menu-label">Height</span>
                      <div className="size-menu-btns">
                        {[1, 2, 3, 4].map((n) => (
                          <button key={n} className={`panel-height-btn${h === n ? ' active' : ''}`} onClick={() => setHeight(panelId, n)}>{n}</button>
                        ))}
                        <button className={`panel-height-btn${h === 0 ? ' active' : ''}`} onClick={() => setHeight(panelId, 0)}>100%</button>
                      </div>
                    </div>
                    <div className="size-menu-row">
                      <span className="size-menu-label">Width</span>
                      <div className="size-menu-btns">
                        {Array.from({ length: columns }, (_, i) => i + 1).map((n) => (
                          <button key={n} className={`panel-height-btn${w === n ? ' active' : ''}`} onClick={() => setWidth(panelId, n)}>{n}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <span
                  className="panel-more-config"
                  onClick={(e) => { e.stopPropagation(); setMoreMenuId(moreMenuId === panelId ? null : panelId); setSizeMenuId(null); }}
                  title="Configure More link"
                >
                  &#x2192;
                </span>
                {moreMenuId === panelId && layouts && (
                  <div className="panel-more-menu" onClick={(e) => e.stopPropagation()}>
                    <span className="size-menu-label">More goes to:</span>
                    <button
                      className={`panel-height-btn${moreTarget === undefined || moreTarget === null ? ' active' : ''}`}
                      onClick={() => setMoreLink(panelId, null)}
                    >
                      None
                    </button>
                    {layouts.map((l, i) => (
                      <button
                        key={i}
                        className={`panel-height-btn${moreTarget === i ? ' active' : ''}`}
                        onClick={() => setMoreLink(panelId, i)}
                      >
                        {l.name}
                      </button>
                    ))}
                  </div>
                )}
                <span
                  className={`panel-minimize${minimized.includes(panelId) ? ' active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); minimized.includes(panelId) ? unminimizePanel(panelId) : minimizePanel(panelId); }}
                  title={minimized.includes(panelId) ? 'Unminimize' : 'Minimize (hide by default)'}
                >
                  {minimized.includes(panelId) ? '\u25B2' : '\u25BC'}
                </span>
                <span
                  className="panel-close"
                  onClick={(e) => { e.stopPropagation(); hidePanel(panelId); }}
                  title="Close panel"
                >
                  &times;
                </span>
                <span className="drag-dots">&#x2630;</span>
              </div>
            )}
            <div className="panel-content">
              {content}
              {!editMode && hasMore && (
                <button
                  className="panel-more-btn"
                  onClick={() => onNavigate && onNavigate(moreTarget)}
                >
                  <span className="panel-more-icon">&#x2192;</span> More
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
    {minimizedPanels.length > 0 && (
      <div className="panel-show-hidden">
        <label className="settings-toggle-label" style={{ fontSize: 13, gap: 6, justifyContent: 'center' }}>
          <input type="checkbox" checked={showMinimized} onChange={(e) => setShowMinimized(e.target.checked)} />
          <span>Show hidden panels ({minimizedPanels.length})</span>
        </label>
      </div>
    )}
    </div>
  );
}
